"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrCadDsnTransformSchematicGenerator = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const fonts_1 = require("../../../../Fonts/fonts");
const TransformProject_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProject");
const TransformProcessingResult_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProcessingResult");
const TransformSchematic_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformSchematic");
const TransformSchematicLayerSet_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformSchematicLayerSet");
const OrcadNetNamingStrategy_1 = require("../OrcadNetNamingStrategy");
const NetCombobulator_1 = require("../../../../NetCombobulation/NetCombobulator");
const TransformMultiInstanceSchematic_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformMultiInstanceSchematic");
const OrcadDsnUtils_1 = require("./OrcadDsnUtils");
const orcadLayers_1 = require("../../../orcadLayers");
const orcadLogicalSchematicReferenceResolver_1 = require("../../../orcadLogicalSchematicReferenceResolver");
class OrCadDsnTransformSchematicGenerator {
    constructor() {
        this.runsFor = [TransformProject_1.TransformProject];
    }
    static { this.COMBOBULATOR_EXPLICIT_WIRE_INTERSECTIONS = true; }
    *generate(parent, rootContext) {
        const context = parent.context;
        const allInstances = yield* this.buildSchematicHierarchyInstances(parent, context, context.rootHierarchies);
        yield* this.generateSchematicsForHierarchyInstances(parent, allInstances, context);
        rootContext.pageSchematicRecords = [
            ...(rootContext.pageSchematicRecords ?? []),
            ...(context.pageSchematicRecords ?? []),
        ];
        const hierarchyNodes = this.buildDesignHierarchyNodes(allInstances, context);
        rootContext.designHierarchyResults = rootContext.designHierarchyResults ?? [];
        rootContext.designHierarchyResults.push(hierarchyNodes);
    }
    *generateSchematicsForHierarchyInstances(project, allInstances, context) {
        context.hierarchyPortDiagnostics = context.hierarchyPortDiagnostics ?? new Set();
        const namingStrategy = new OrcadNetNamingStrategy_1.OrCadNetNamingStrategy(context.options.debugNetNamingChain, (message) => context.hierarchyPortDiagnostics?.add(message));
        for (const [hierarchy, instances] of allInstances) {
            let multiInstanceSchematic;
            if (instances.length > 1) {
                const pageNumbers = new Set(instances.map((i) => this.getInstancePageNumber(i, 'Schematic Page Number')));
                const pageNumber = pageNumbers.size === 1 ? Array.from(pageNumbers)[0] : 1;
                if (pageNumbers.size > 1) {
                    yield new TransformProcessingResult_1.TransformProcessingResult(project, {
                        type: 'orcad-dsn-transformer',
                        message: `Multiple instances of the same Design+Schematic+Page found, but with different page numbers. This is not supported. Will use the first page number: ${pageNumber}`,
                        severity: sigma_engine_shared_1.BoundaryLevel.Error,
                        pageNumbers: Array.from(pageNumbers),
                    });
                }
                const multiInstanceCombobulator = new NetCombobulator_1.NetCombobulator(new sigma_engine_shared_1.NetRefCtx(), namingStrategy, {
                    ...instances[0],
                    isMultiInstanceSheet: true,
                }, {
                    explicitWireIntersections: OrCadDsnTransformSchematicGenerator.COMBOBULATOR_EXPLICIT_WIRE_INTERSECTIONS,
                });
                const isExternalDsnMulti = hierarchy.design !== context.rootDesign;
                const multiExternalSourceFile = context.designSourceFiles.get(hierarchy.design) ?? hierarchy.sourceFile;
                const multiFilePrefix = isExternalDsnMulti ? `${multiExternalSourceFile}: ` : '';
                multiInstanceSchematic = new TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic(project, {
                    designId: (0, OrcadDsnUtils_1.orcadDesignId)(hierarchy.name),
                    designName: `${multiFilePrefix}${hierarchy.schematic.Name} - ${hierarchy.page.Name}`,
                    sheetIndex: pageNumber,
                    sourceFile: hierarchy.sourceFile,
                    instances,
                    sheetDisplayPath: [],
                }, multiInstanceCombobulator, context);
                yield multiInstanceSchematic;
            }
            for (const instance of instances) {
                const netCb = new NetCombobulator_1.NetCombobulator(instance.netRefCtx, namingStrategy, instance, {
                    explicitWireIntersections: OrCadDsnTransformSchematicGenerator.COMBOBULATOR_EXPLICIT_WIRE_INTERSECTIONS,
                });
                const schematic = new TransformSchematic_1.TransformSchematic(multiInstanceSchematic ?? project, {
                    designId: (0, OrcadDsnUtils_1.orcadDesignId)(`${instance.hierarchyEntry.slug}|${instance.pathUniqueId}`),
                    designName: instance.name,
                    sheetIndex: this.getInstancePageNumber(instance, 'Page Number'),
                    sourceFile: instance.hierarchyEntry.sourceFile,
                    font: { default: fonts_1.Font.Newstroke },
                    instance,
                }, netCb, context);
                yield schematic;
                yield this.createLayerSet(schematic);
                context.instanceSchematicMap.set(instance, schematic);
                context.pageSchematicRecords = context.pageSchematicRecords ?? [];
                context.pageSchematicRecords.push({ page: hierarchy.page, schematic });
            }
        }
    }
    getInstancePageNumber(i, numberType) {
        const foundNumbers = new Set((0, sigma_engine_shared_1.toArray)(i.instOccurence.TitleBlockOccurrence)
            .map((t) => OrcadDsnUtils_1.OrcadDsnUtils.getTitleBlockUserProp(t, numberType)?.Value)
            .filter((v) => v !== undefined)
            .map((v) => parseInt(v, 10)));
        if (foundNumbers.size === 0) {
            return 1;
        }
        return Array.from(foundNumbers)[0] ?? 1;
    }
    *buildSchematicHierarchyInstances(project, context, rootHierarchies, hierarchyInstances = new Map(), path = [], pathUniqueId = [], sheetSymbol) {
        for (const rootHierarchy of rootHierarchies) {
            if (path.some((ancestor) => ancestor.hierarchyEntry === rootHierarchy)) {
                const joinedPath = pathUniqueId.join('/');
                yield new TransformProcessingResult_1.TransformProcessingResult(project, {
                    type: 'orcad-dsn-transformer',
                    message: `Self-referential hierarchy skipped for ${rootHierarchy.design.Name} - ${rootHierarchy.schematic.Name} - ${rootHierarchy.page.Name}.`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                    pathUniqueId: joinedPath.length > 0 ? joinedPath : '/',
                });
                continue;
            }
            const parentInstOccurences = path.length > 0 ?
                path[path.length - 1].instOccurence.InstOccurrence
                : context.rootDesign.Occurrences.InstOccurrence;
            if (!parentInstOccurences) {
                throw new Error(`No parent inst occurences found`);
            }
            const myInstOccurence = parentInstOccurences.find((o) => o.RefInstId === (sheetSymbol?.DbId.toString() ?? '-1'));
            if (!myInstOccurence) {
                throw new Error(`No inst occurence found for ${sheetSymbol?.DbId.toString() ?? '-1'} in ${parentInstOccurences.map((o) => o.RefInstId).join(', ')}`);
            }
            const designator = sheetSymbol ?
                (myInstOccurence.UserProperty?.find((p) => p.Name === 'Reference')?.Value ?? sheetSymbol.DeviceDesignator)
                : '';
            const myPathUniqueIdParts = [...pathUniqueId, `${sheetSymbol?.DbId.toString() ?? ''}_${designator}`];
            const myPathUniqueId = myPathUniqueIdParts.join('/') || '/';
            const schematicPath = OrcadDsnUtils_1.OrcadDsnUtils.getTitleBlockUserProp(myInstOccurence.TitleBlockOccurrence, 'Schematic Path')?.Value;
            if (!schematicPath) {
                yield new TransformProcessingResult_1.TransformProcessingResult(project, {
                    type: 'orcad-dsn-transformer',
                    message: `No schematic path found for ${rootHierarchy.design.Name} - ${rootHierarchy.schematic.Name} - ${rootHierarchy.page.Name}. Could not validate pathUniqueId.`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                    pathUniqueId: myPathUniqueId,
                });
            }
            const isExternalDsn = rootHierarchy.design !== context.rootDesign;
            const externalSourceFile = context.designSourceFiles.get(rootHierarchy.design) ?? rootHierarchy.sourceFile;
            const filePrefix = isExternalDsn ? `${externalSourceFile}: ` : '';
            const baseName = `${filePrefix}${rootHierarchy.schematic.Name} - ${rootHierarchy.page.Name}`;
            const isRoot = path.length === 0;
            let displayName;
            if (isRoot) {
                displayName = baseName;
            }
            else {
                const pathSegments = [
                    ...path.map((p) => ({ designator: p.designator, sheetSymbol: p.sheetSymbol })),
                    { designator, sheetSymbol },
                ];
                const readablePath = pathSegments
                    .map((seg) => {
                    if (!seg.designator) {
                        return '';
                    }
                    return seg.designator.includes('?') && seg.sheetSymbol ?
                        `${seg.designator}#${seg.sheetSymbol.DbId}`
                        : seg.designator;
                })
                    .filter(Boolean)
                    .join('/');
                displayName = readablePath ? `${baseName} (/${readablePath})` : baseName;
            }
            const instance = {
                name: displayName,
                designator,
                hierarchyEntry: rootHierarchy,
                path: Array.from(path),
                pathUniqueId: myPathUniqueId,
                numInstances: 1,
                totalSheetInstances: 0,
                instanceIndex: hierarchyInstances.get(rootHierarchy)?.length ?? 0,
                sheetSymbol,
                isMultiInstanceSheet: false,
                netRefCtx: new sigma_engine_shared_1.NetRefCtx(),
                instOccurence: myInstOccurence,
                children: [],
            };
            const arr = hierarchyInstances.get(rootHierarchy) ?? [];
            arr.push(instance);
            hierarchyInstances.set(rootHierarchy, arr);
            const children = context.pageChildren.get(rootHierarchy) ?? [];
            for (const child of children) {
                const candidates = Array.from(new Set(context.allHierarchies.map((hierarchy) => hierarchy.sourceFile)), (sourceFile) => ({
                    sourceFile,
                    schematicViewNames: context.allHierarchies
                        .filter((hierarchy) => hierarchy.sourceFile === sourceFile)
                        .map((hierarchy) => hierarchy.schematic.Name),
                }));
                const parentStem = (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(rootHierarchy.sourceFile).replace(/\.(dsn|xml)$/i, '');
                const childStem = (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(child.targetFile).replace(/\.(dsn|xml)$/i, '');
                const resolution = parentStem === childStem ? undefined : ((0, orcadLogicalSchematicReferenceResolver_1.resolveOrCadLogicalSchematicReference)(candidates, rootHierarchy.sourceFile, child.targetFile, child.targetSchematic));
                const targetSourceFile = parentStem === childStem ? (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(rootHierarchy.sourceFile)
                    : resolution?.status === 'resolved' ? (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(resolution.candidate.sourceFile)
                        : undefined;
                const childHierarchies = context.allHierarchies.filter((hierarchy) => targetSourceFile !== undefined &&
                    (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(hierarchy.sourceFile) === targetSourceFile &&
                    hierarchy.schematic.Name.toLowerCase() === child.targetSchematic.toLowerCase());
                yield* this.buildSchematicHierarchyInstances(project, context, childHierarchies, hierarchyInstances, [...path, instance], myPathUniqueIdParts, child.sheetSymbol);
            }
        }
        if (path.length === 0) {
            for (const [_hierarchy, instances] of hierarchyInstances) {
                for (const instance of instances) {
                    if (instance.totalSheetInstances !== 0 && instance.totalSheetInstances !== instances.length) {
                        yield new TransformProcessingResult_1.TransformProcessingResult(project, {
                            type: 'orcad-dsn-transformer',
                            message: `Total sheet instances mismatch for ${instance.name}. Expected: ${instances.length}, Found: ${instance.totalSheetInstances}`,
                            severity: sigma_engine_shared_1.BoundaryLevel.Error,
                            expected: instances.length,
                            found: instance.totalSheetInstances,
                        });
                    }
                    instance.totalSheetInstances = instances.length;
                    const lastParent = instance.path[instance.path.length - 1];
                    if (lastParent) {
                        lastParent.children.push(instance);
                    }
                }
            }
        }
        return hierarchyInstances;
    }
    buildDesignHierarchyNodes(allInstances, context) {
        const rootSourceFileKey = context.designSourceFiles.get(context.rootDesign) ?? '';
        const byFile = new Map();
        for (const hierarchy of context.allHierarchies) {
            const sourceFileKey = context.designSourceFiles.get(hierarchy.design) ?? hierarchy.sourceFile;
            let bySchematic = byFile.get(sourceFileKey);
            if (!bySchematic) {
                bySchematic = new Map();
                byFile.set(sourceFileKey, bySchematic);
            }
            const schName = hierarchy.schematic.Name;
            const arr = bySchematic.get(schName) ?? [];
            arr.push(hierarchy);
            bySchematic.set(schName, arr);
        }
        const fileNodes = [];
        for (const [sourceFileKey, bySchematic] of byFile) {
            const isRoot = sourceFileKey === rootSourceFileKey;
            const fileLabel = isRoot ? sigma_engine_shared_1.Files.basenamePath(sourceFileKey) : sourceFileKey;
            const schematicNodes = [];
            for (const [schName, hierarchies] of bySchematic) {
                if (hierarchies.length === 1) {
                    const pageNode = this.buildPageNode(hierarchies[0], allInstances, context);
                    schematicNodes.push({
                        ...pageNode,
                        label: schName,
                    });
                }
                else {
                    const pageNodes = [];
                    for (const hierarchy of hierarchies) {
                        pageNodes.push(this.buildPageNode(hierarchy, allInstances, context));
                    }
                    schematicNodes.push({
                        label: schName,
                        children: pageNodes,
                    });
                }
            }
            fileNodes.push({
                label: fileLabel,
                children: schematicNodes,
            });
        }
        return fileNodes;
    }
    buildPageNode(hierarchy, allInstances, context) {
        const instances = allInstances.get(hierarchy) ?? [];
        if (instances.length > 1) {
            const instanceChildren = instances.map((inst) => {
                const instSchematic = context.instanceSchematicMap.get(inst);
                const instLabel = this.buildInstanceLabel(inst);
                return {
                    designId: (0, OrcadDsnUtils_1.orcadDesignId)(hierarchy.name),
                    label: instLabel,
                    instanceId: instSchematic?.designId,
                };
            });
            return {
                designId: (0, OrcadDsnUtils_1.orcadDesignId)(hierarchy.name),
                label: hierarchy.page.Name,
                children: instanceChildren,
            };
        }
        const inst = instances[0];
        const schematic = inst ? context.instanceSchematicMap.get(inst) : undefined;
        return {
            designId: schematic?.designId,
            label: hierarchy.page.Name,
        };
    }
    buildInstanceLabel(inst) {
        const parts = [...inst.path, inst].map((p) => p.designator).filter(Boolean);
        return parts.length > 0 ? `(/${parts.join('/')})` : inst.name;
    }
    createLayerSet(schematic) {
        const layers = orcadLayers_1.OrCadSchLayers.map((layer) => ({
            ...layer,
            defaultVisible: typeof layer.defaultVisible !== 'boolean' || layer.defaultVisible === true,
        }));
        return new TransformSchematicLayerSet_1.TransformSchematicLayerSet(schematic, {
            type: 'layerSet',
            id: orcadLayers_1.ORCAD_SCH_LAYERSET,
            layerSetType: 'schematic',
            layers,
        });
    }
}
exports.OrCadDsnTransformSchematicGenerator = OrCadDsnTransformSchematicGenerator;
//# sourceMappingURL=OrcadDsnTransformSchematicGenerator.js.map